import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { Navigate, Outlet, useLocation, useSearchParams, matchPath } from 'react-router-dom'
import { apiGet, apiPost, apiPatch, apiPut, apiDelete, apiPostForm, api, streamPost, setCsrfToken, primeSessionCsrf, ApiError } from './api'
import type { Me } from '../types/auth'
import i18n from 'i18next'
import { toast } from '../components/ui/toast'
import { isPlatformAdmin, hasNoAccess, isClientRole } from './roles'
import BootScreen from '../components/BootScreen'

// Shared auth contract (ENG-194). Both apps talk to the same Devise/Warden
// session over the same `claritio_shared` User row, so the provider, the org-scoping
// hooks and the route guards are one implementation rather than two copies.
//
// Where the apps genuinely differ, the difference is a required prop rather than
// a default — see RequireOrg. ENG-202 calls this out specifically: second-brain
// has no default-org fallback in v0, and a guard that silently redirected there
// would paper over that rather than preserve it.

interface AuthState {
  me: Me | null
  loading: boolean
  refresh: () => Promise<void>
  login: (email: string, password: string) => Promise<void>
  signup: (email: string, password: string, passwordConfirmation: string, role?: string) => Promise<void>
  // Resolves false (after a toast) when the server did not end the session; callers stay put.
  logout: () => Promise<boolean>
  employeeSignup: (token: string, name: string, password: string, passwordConfirmation: string) => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null)
  const [loading, setLoading] = useState(true)

  async function refresh() {
    try {
      setMe(await apiGet<Me>('/api/v1/me'))
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) setMe(null)
      else throw e
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // refresh() is async and every setState in it happens after an await, so
    // nothing here is actually synchronous — the rule traces into the callee and
    // cannot see the await boundary. Rewriting it as a .then chain would silence
    // the rule without changing anything at runtime, so it stays as it is.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    refresh()
  }, [])

  const login = async (email: string, password: string) => {
    setMe(await apiPost<Me>('/api/v1/session', { user: { email, password } }))
    // Devise's clean_up_csrf_token_on_authentication deletes session[:_csrf_token]
    // on sign_in. ai-assessment self-heals because every subsequent response body
    // carries a fresh csrf_token that request() adopts automatically, but
    // second-brain's GET responses never carry one — so the client-side token
    // primed for it pre-login goes stale the moment sign-in clears the session's
    // CSRF secret. Re-prime now (other origins re-fetch on first use), same fix as logout() below.
    await primeSessionCsrf()
  }
  // signup/employeeSignup hit ai-assessment-only endpoints. They stay on the
  // shared context because ai-assessment is the sole sign-in authority for the
  // shared session (ENG-170 §1) — second-brain simply never calls them.
  const signup = async (email: string, password: string, passwordConfirmation: string, role = 'consultant') => {
    setMe(
      await apiPost<Me>('/api/v1/registration', {
        user: { email, password, password_confirmation: passwordConfirmation, role },
      }),
    )
    // Same clean_up_csrf_token_on_authentication issue as login() above.
    await primeSessionCsrf()
  }
  const logout = async () => {
    try {
      await apiDelete('/api/v1/session')
    } catch {
      toast(i18n.t('common:action.signOutFailed'), { tone: 'error' })
      return false
    }
    setMe(null)
    // SessionsController#destroy returns `head :no_content` — no body, so no
    // rotated csrf_token comes back the way every other write's response
    // carries one (see request() in api.ts). Cross-origin, primeCsrf() is the
    // only thing that ever seeded a token in the first place (no <meta> tag to
    // fall back to), so without re-priming here the map is left holding
    // whatever token was live before logout. A same-browser-session sign-in
    // right after would POST /api/v1/session with that stale token and fail
    // CSRF verification. Same-origin this is a harmless no-op (primeSessionCsrf() primes nothing
    // when no cross-origin base is configured).
    await primeSessionCsrf()
    return true
  }

  async function employeeSignup(token: string, name: string, password: string, passwordConfirmation: string) {
    const data = await apiPost<Me>("/api/v1/employee_registration", {
      token,
      name,
      password,
      password_confirmation: passwordConfirmation,
    })
    setCsrfToken(data.csrf_token)
    setMe(data)
    // Same clean_up_csrf_token_on_authentication issue as login() above.
    await primeSessionCsrf()
  }

  return (
    <AuthContext.Provider value={{ me, loading, refresh, login, signup, logout, employeeSignup }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}

// ENG-113: the current org is read straight from the URL, never from React
// state — a refresh, a shared link, or a new tab all see the same thing, since
// there's nothing to go stale. Every workspace route requires ?org_id= (see
// RequireOrg below); pages needing it call this instead of holding their own copy.
export function useOrgId(): string | null {
  const [params] = useSearchParams()
  return params.get('org_id')
}

// ENG-116: workspace chrome (sidebar client zone, breadcrumb, switcher recents)
// must survive the Interview Library and the client's settings, where the org sits
// in the PATH (/interviews/:id per ENG-97, /clients/:id) rather than the query. Still URL-derived — no
// hidden state. RequireOrg keeps using the strict query-only useOrgId().
export function useWorkspaceOrgId(): string | null {
  const queryOrgId = useOrgId()
  const { pathname } = useLocation()
  if (queryOrgId) return queryOrgId
  return (matchPath('/interviews/:id', pathname) ?? matchPath('/clients/:id', pathname))?.params.id ?? null
}

// Context-bound client for org-scoped endpoints — orgId is read from the URL
// once per call and can never be omitted, unlike a trailing optional argument.
// Throws if used outside a RequireOrg-guarded route (there is no valid orgId
// to bind), so a misuse fails immediately instead of silently sending no
// org_id and relying on the backend's fallback.
export function useOrgApi() {
  return orgApiFor(useOrgId())
}

// The same client for an org that is not in the query, e.g. Clara's panel on /interviews/:id.
export function orgApiFor(orgId: string | null) {
  if (!orgId) throw new Error('useOrgApi() called outside a RequireOrg-guarded route')
  return {
    get:    <T,>(path: string) => apiGet<T>(path, orgId),
    post:   <T,>(path: string, payload?: unknown) => apiPost<T>(path, payload, orgId),
    patch:  <T,>(path: string, payload?: unknown) => apiPatch<T>(path, payload, orgId),
    put:    <T,>(path: string, payload?: unknown) => apiPut<T>(path, payload, orgId),
    delete: <T,>(path: string) => apiDelete<T>(path, orgId),
    upload: <T,>(path: string, form: FormData) => apiPostForm<T>(path, form, orgId),
    call:   <T,>(path: string, options?: { method?: string; body?: string }) => api<T>(path, options, orgId),
    stream: <E,>(path: string, payload: unknown, onEvent: (event: E) => void) => streamPost<E>(path, payload, onEvent, orgId),
  }
}

export function RequireAuth({ loginPath = '/login' }: { loginPath?: string } = {}) {
  const { me, loading } = useAuth()
  const location = useLocation()
  if (loading) return <BootScreen />
  if (!me?.user) return <Navigate to={loginPath} replace state={{ from: location }} />
  return <Outlet />
}

export function RequireAdmin() {
  const { me, loading } = useAuth()
  const location = useLocation()
  if (loading) return <BootScreen />
  if (!me?.user) return <Navigate to="/login" replace state={{ from: location }} />
  // The operator area is for everyone who isn't a client-side employee: platform
  // admins, consultants, and freshly-signed-up operators who have no org
  // membership yet (role === null) and bootstrap their first client from
  // /clients (ClientsController#create). Fresh signups outside Claritio go to
  // /no-access instead (hasNoAccess). Only a client-side (`employee` or `sponsor`) membership
  // belongs on the employee dashboard — gating on `role === 'consultant'` here
  // instead trapped fresh signups in a redirect loop.
  if (hasNoAccess(me)) return <Navigate to="/no-access" replace />
  const isAdmin = isPlatformAdmin(me)
  if (!isAdmin && isClientRole(me.user.role)) return <Navigate to="/employee/dashboard" replace />
  return <Outlet />
}

// Second Brain v0 (trimmed ENG-202) is platform-admin-only — unlike
// RequireAdmin (which admits every operator: admins and consultants), this
// checks the actual platform_role flag. Consultants keep seeing the same
// "coming soon" placeholder they see today; this only ever narrows who sees
// the real pages, never redirects anyone away from the app.
export function RequirePlatformAdmin({ fallback }: { fallback: ReactNode }) {
  const { me, loading } = useAuth()
  // The loading check matters: without it, a platform admin's first render
  // (before /api/v1/me resolves) would flash the fallback before flipping to
  // the real page. RequireAdmin above this in the route tree has already
  // handled "not logged in", but this component is still reachable while `me`
  // is being fetched, so it needs its own check.
  if (loading) return <BootScreen />
  if (!isPlatformAdmin(me)) return <>{fallback}</>
  return <Outlet />
}

export function RequireEmployee() {
  const { me, loading } = useAuth()
  if (loading) return <BootScreen />
  if (!me?.user) return <Navigate to="/employee/login" replace />
  if (!isClientRole(me.user.role)) return <Navigate to="/" replace />
  return <Outlet />
}

// ENG-113: gates every workspace route on an explicit ?org_id= being present in
// the URL. There was previously no guard here at all — a route with no org
// context just silently fell back to an arbitrary membership server-side.
//
// `fallback` is required rather than defaulted because the two apps legitimately
// disagree about what "no org in the URL" means, and the difference has to stay
// visible at the call site:
//
//   * ai-assessment redirects to /clients, the picker whose whole job is to
//     produce an org_id.
//   * second-brain has no picker and no default-org fallback in v0 (ENG-79), so
//     it renders an explicit message instead. Redirecting it to /clients would
//     land on a route that does not exist there.
export function RequireOrg({ fallback }: { fallback: ReactNode }) {
  const orgId = useOrgId()
  if (!orgId) return <>{fallback}</>
  return <Outlet />
}
